import {
    DrawerFooter,
    DrawerHeader,
    Flex,
    HStack,
    ButtonGroup,
    DrawerBody,
    VStack,
    Button,
    InlineBanner,
    Text,
    Box,
    Input,
    Drawer,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useAsyncManageUnitsDrawerData } from './use-async-manage-units-drawer-data';
import { ManageUnitsUnitGroupEnum, ManageUnitsUnitGroupEnumType } from '@mark43/cad-api';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { FilterPopover } from './common/filter-popover/filter-popover';
import { SortMenu } from './common/sort-menu';
import { UnitsSummary } from './common/units-summary';
import { EventSummary } from './components/event-summary';
import { convertManageUnitsUnitViewToPillUnit } from './convert-manage-units-unit-view-to-pill-unit';
import { GroupHeader } from './common/group-header';
import { BannersDisplay } from './common/banners-display';
import { globalAttributes } from '../common/global-attributes';
import { isNotNullOrUndefined } from '../queues/utils/is-not-null-or-undefined';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { UnitTile } from '../common/components/unit-tile';
import { useKeyboardNavigation } from '../common/hooks/use-tile-keyboard-navigation';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { v4 } from 'uuid';
import { createUnitSortComparator } from './utils';
import {
    computeEventStaffCount,
    getSuggestedStaffCountForDispatch,
    isDispatchStaffCountWriteEnabled,
} from './common/suggested-staff-count';
import { useSubmitFormOnEnter } from '../common/hooks/use-submit-form-on-enter';
import { useApplicationType } from '../common/hooks/use-application-type';
import { RecommendationPeekChip } from './recommendation-peek-chip';
import {
    RecommendedSectionHeader,
    RecommendedSectionHeaderContext,
    RecommendedSectionHeaderState,
} from './recommended-section-header';
import {
    RecommendedSectionMode,
    useRecommendedSectionVisibility,
} from './use-recommended-section-visibility';
import { useAsyncResponsePlanContext } from './store/async-response-plan-context';

export interface KeyboardNavState {
    index?: number;
    isOverPrimary: boolean;
}

// While a recommendation *refresh* is actively in flight, dim the results so the dispatcher sees an
// "updating" signal while the last good plan stays visible. Keyed on `isRefreshing` (not
// `status === 'pending'`): a refresh that times out or fails silently leaves `status` at `'pending'`
// so a late push can still recover, but `isRefreshing` goes false — so a timed-out refresh does not
// leave the list dimmed indefinitely. First load shows its own loading placeholders, not the dim.
export function refreshDimOpacity(isRefreshing: boolean): number {
    return isRefreshing ? 0.6 : 1;
}

export const AsyncManageUnitsDrawer = observer(function AsyncManageUnitsDrawer({
    eventId,
}: {
    eventId: number;
}): React.ReactElement {
    const drawerControls = useDrawerControls();

    const {
        store,
        event,
        debouncedSetQuery,
        sortingParam,
        setSortingParam,
        sortDirection,
        setSortDirection,
        selectedFilters,
        setSelectedFilters,
        hasSelectedFilters,
        toggleSelectUnit,
        initiallyDispatchedUnitIds,
        selectedUnitIds,
        releasedUnitIds,
        primaryUnitId,
        primaryDispositionInfo,
        setPrimaryUnitIdAndSelectIfNecessary,
        submit,
        banners,
        responsePlan,
        isEventQueryLoading,
        isUnitsQueryLoading,
        isUnitsQueryError,
        isMutationLoading,
        areUnitsFetchedAfterMount,
    } = useAsyncManageUnitsDrawerData(eventId);

    const openEventPanel = useOpenEventPanel();
    const enabledFeatures = useEnabledFeatures();
    const { staffCountEnabled, unitLevelDispositionsEnabled } = enabledFeatures;
    const staffCountWriteEnabled = isDispatchStaffCountWriteEnabled(enabledFeatures);

    // Response-plan status comes from the provider context, not the data hook.
    const {
        status: responsePlanStatus,
        isFirstLoad: isResponsePlanFirstLoad,
        isRefreshing: isResponsePlanRefreshing,
        retry: retryResponsePlan,
    } = useAsyncResponsePlanContext();

    const rows = store.data.list.slice();
    const groupCounts = store.data.groupCounts.slice();
    const groupHeaders = store.data.groupHeaders.slice();
    const [drawerContentElement, setDrawerContentElement] = useState<HTMLElement | null>(null);

    // Async response-plan UI for the Recommended section. The Recommended section is the source
    // of truth and lives INSIDE the list — the Virtuoso Header holds the loading/failed
    // placeholders, and the store's RECOMMENDED group holds the recs once they land. The floating
    // peek chip only mirrors the section and only shows while it is >50% scrolled out of view.
    const recommendedSectionHeaderState: RecommendedSectionHeaderState =
        responsePlanStatus === 'pending' && isResponsePlanFirstLoad
            ? 'loading'
            : responsePlanStatus === 'failed' && isResponsePlanFirstLoad
              ? 'failed'
              : 'none';

    // Peek-chip visibility inputs. RECOMMENDED is the first group, so recommended rows are item
    // indices 0..count-1; their count (when landed) is groupCounts[0] iff the first group is
    // RECOMMENDED. This is a positional coupling to the store's group ordering: if RECOMMENDED is
    // ever not first, recommendedCount falls to 0 and the peek chip simply never shows (safe, silent
    // degradation — no chip, not a broken chip).
    const recommendedCount = groupHeaders[0] === 'RECOMMENDED' ? (groupCounts[0] ?? 0) : 0;
    const [scrollerEl, setScrollerEl] = useState<HTMLElement | null>(null);
    const [gatheringHeaderEl, setGatheringHeaderEl] = useState<HTMLElement | null>(null);
    const [visibleStartIndex, setVisibleStartIndex] = useState(0);
    const sectionVisibilityMode: RecommendedSectionMode =
        responsePlanStatus === 'pending' && isResponsePlanFirstLoad
            ? 'loading'
            : responsePlanStatus === 'landed'
              ? 'landed'
              : 'none';
    const { outOfView: recommendedSectionOutOfView } = useRecommendedSectionVisibility({
        mode: sectionVisibilityMode,
        scrollerEl,
        headerEl: gatheringHeaderEl,
        recommendedCount,
        visibleStartIndex,
    });

    // Acknowledgement of the current rec load: forced false whenever not `landed` (a new load
    // re-arms), set true once the dispatcher views the landed recs (section back in view) or clicks
    // the ready chip.
    const [recsAcknowledged, setRecsAcknowledged] = useState(false);
    useEffect(
        function resetRecsAcknowledgementOnNewLoad() {
            if (responsePlanStatus !== 'landed') {
                setRecsAcknowledged(false);
            }
        },
        [responsePlanStatus]
    );
    useEffect(
        function acknowledgeWhenRecommendedSectionViewed() {
            if (responsePlanStatus === 'landed' && !recommendedSectionOutOfView) {
                setRecsAcknowledged(true);
            }
        },
        [responsePlanStatus, recommendedSectionOutOfView]
    );

    const showLoadingPeekChip =
        responsePlanStatus === 'pending' &&
        isResponsePlanFirstLoad &&
        recommendedSectionOutOfView;
    const showReadyPeekChip =
        responsePlanStatus === 'landed' && recommendedSectionOutOfView && !recsAcknowledged;

    const alreadyDispatchedUnits = useMemo(
        () =>
            initiallyDispatchedUnitIds
                .map((id) => store.getItemById(id))
                .filter(isNotNullOrUndefined)
                .sort(createUnitSortComparator(primaryUnitId))
                .map(convertManageUnitsUnitViewToPillUnit),
        [initiallyDispatchedUnitIds, primaryUnitId, store]
    );

    const toDispatchUnits = useMemo(
        () =>
            selectedUnitIds
                .filter((id) => {
                    return !initiallyDispatchedUnitIds.includes(id);
                })
                .map((id) => store.getItemById(id))
                .filter(isNotNullOrUndefined)
                .sort(createUnitSortComparator(primaryUnitId))
                .map(convertManageUnitsUnitViewToPillUnit),
        [selectedUnitIds, initiallyDispatchedUnitIds, primaryUnitId, store]
    );

    const initiallyDispatchedUnitIdSet = useMemo(
        () => new Set(initiallyDispatchedUnitIds),
        [initiallyDispatchedUnitIds]
    );

    // Counts every unit that will be on the event after Save, using the suggested crew for newly
    // dispatched recommended units (what dispatch writes) and the stored count otherwise. Computed on
    // each render, not memoized, so the observer picks up live staff count changes on the store rows.
    const eventStaffCount = staffCountEnabled
        ? computeEventStaffCount(
              selectedUnitIds.map((id) => store.getItemById(id)).filter(isNotNullOrUndefined),
              alreadyDispatchedUnits,
              initiallyDispatchedUnitIdSet,
              staffCountWriteEnabled
          )
        : undefined;

    const canFocusPrimary = useCallback(() => {
        return true;
    }, []);

    const keyboardTileNavigation = useKeyboardNavigation({
        store,
        onSelectItem: toggleSelectUnit,
        onSetPrimaryItem: setPrimaryUnitIdAndSelectIfNecessary,
        element: drawerContentElement,
        canFocusPrimary,
    });

    const scrollToRecommendedSection = useCallback(
        function scrollToRecommendedSection() {
            keyboardTileNavigation.virtuosoRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [keyboardTileNavigation.virtuosoRef]
    );

    const handleReadyPeekChipClick = useCallback(
        function handleReadyPeekChipClick() {
            scrollToRecommendedSection();
            setRecsAcknowledged(true);
        },
        [scrollToRecommendedSection]
    );

    const handleScrollerRef = useCallback(function handleScrollerRef(
        el: HTMLElement | Window | null
    ) {
        setScrollerEl(el instanceof HTMLElement ? el : null);
    }, []);
    const handleRangeChanged = useCallback(function handleRangeChanged(range: {
        startIndex: number;
    }) {
        setVisibleStartIndex(range.startIndex);
    }, []);
    const registerGatheringHeaderEl = useCallback(function registerGatheringHeaderEl(
        el: HTMLElement | null
    ) {
        setGatheringHeaderEl(el);
    }, []);

    const recommendedSectionComponents = useMemo(() => ({ Header: RecommendedSectionHeader }), []);
    const recommendedSectionContext: RecommendedSectionHeaderContext = useMemo(
        () => ({
            state: recommendedSectionHeaderState,
            onRetry: retryResponsePlan,
            registerEl: registerGatheringHeaderEl,
        }),
        [recommendedSectionHeaderState, retryResponsePlan, registerGatheringHeaderEl]
    );

    // The inline Recommended-section Header, the scroller + visible-range signals the peek chip
    // needs, and the section status context.
    const asyncVirtuosoProps = {
        scrollerRef: handleScrollerRef,
        rangeChanged: handleRangeChanged,
        components: recommendedSectionComponents,
        context: recommendedSectionContext,
    };

    const closeDrawer = useCallback(() => {
        drawerControls.close();
    }, [drawerControls]);

    const handleSubmit = useCallback(() => {
        submit({
            confirmationAcknowledged: false,
            assignAndHold: false,
        });
    }, [submit]);

    useSubmitFormOnEnter({
        element: drawerContentElement,
        onSubmit: handleSubmit,
    });

    const renderItemContent = (index: number): React.ReactElement => {
        const unit = store.data.list[index];

        if (!unit) {
            return <></>;
        }

        return (
            <Box paddingRight="16px">
                <UnitTile
                    unit={unit}
                    toggleSelect={toggleSelectUnit}
                    isSelected={selectedUnitIds.includes(unit.id)}
                    key={index}
                    isFocused={index === keyboardTileNavigation.currentIndex}
                    isFocusingLeft={keyboardTileNavigation.isFocusingLeft}
                    isFocusingRight={keyboardTileNavigation.isFocusingRight}
                    primaryUnitId={primaryUnitId}
                    setPrimaryUnitIdAndSelectIfNecessary={setPrimaryUnitIdAndSelectIfNecessary}
                    showStaffCount
                    suggestedStaffCount={
                        selectedUnitIds.includes(unit.id)
                            ? getSuggestedStaffCountForDispatch(
                                  unit,
                                  initiallyDispatchedUnitIdSet,
                                  staffCountWriteEnabled
                              )
                            : undefined
                    }
                />
            </Box>
        );
    };

    function renderGroupContent(index: number): React.ReactElement {
        return (
            <GroupHeader
                displayName={
                    ManageUnitsUnitGroupEnum[groupHeaders[index] as ManageUnitsUnitGroupEnumType]
                        .displayName
                }
                count={groupCounts[index]}
            />
        );
    }

    const drawerTitle = (
        <Box mt="1" mb="3">
            <Text variant="headingMd">Manage Units</Text>
        </Box>
    );

    const [hasOpenedEventView, setHasOpenedEventView] = useState(false);
    const workspacePanelsStore = useWorkspaceStore();
    const { trigger } = useIntegratedActionBroadcaster();
    const { isElectron } = useApplicationType();

    useEffect(() => {
        const activeWorkspaceWindowType = workspacePanelsStore.activeWorkspaceWindowType;
        const baseShouldOpenEventPanelAndFocusUnitTab =
            !hasOpenedEventView &&
            // Wait for this request to finish before opening the unit tab: it's the request that
            // actually persists a unit recommendation (if there is one), so the recommendation it
            // generates is present by the time the tab opens.
            areUnitsFetchedAfterMount &&
            (responsePlan ||
                event?.agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.FIRE);

        function openEventPanelAndFocusUnitTab() {
            openEventPanel({
                dontFocusWindow: true,
                eventId,
            });

            trigger([
                {
                    id: v4(),
                    targetWindowType: 'EVENT_V2',
                    type: 'SET_EVENT_PANEL_TAB',
                    payload: {
                        eventId,
                        tab: 'UNITS',
                    },
                },
            ]);

            setHasOpenedEventView(true);
        }

        if (baseShouldOpenEventPanelAndFocusUnitTab) {
            switch (activeWorkspaceWindowType) {
                // Opened from a queue or a map: only open the events panel in Electron.
                case 'QUEUE_V2':
                case 'MAP_V2': {
                    if (isElectron) {
                        openEventPanelAndFocusUnitTab();
                    }
                    break;
                }
                case 'EVENT_V2': {
                    openEventPanelAndFocusUnitTab();
                    break;
                }
            }
        }
    }, [
        hasOpenedEventView,
        areUnitsFetchedAfterMount,
        event,
        responsePlan,
        eventId,
        openEventPanel,
        trigger,
        workspacePanelsStore,
        isElectron,
    ]);

    return (
        <Drawer
            isOpen
            size="lg"
            variant="overlay"
            onClose={closeDrawer}
            closeOnOverlayClick={false}
        >
            <Flex height="100%" direction="column" ref={setDrawerContentElement}>
                {isEventQueryLoading ? (
                    <>
                        <DrawerHeader>{drawerTitle}</DrawerHeader>
                        <DrawerBody>
                            <LoadingIndicator />
                        </DrawerBody>
                    </>
                ) : (
                    <>
                        <DrawerHeader>
                            {drawerTitle}
                            {event && (
                                <>
                                    <VStack align="left">
                                        <EventSummary
                                            {...(staffCountEnabled ? { eventStaffCount } : {})}
                                            event={event}
                                            resourcePatternString={
                                                responsePlan?.resourcePatternString
                                            }
                                        />
                                        <Box marginTop="0">
                                            {!!initiallyDispatchedUnitIds.length && (
                                                <UnitsSummary
                                                    label="Already Dispatched"
                                                    units={alreadyDispatchedUnits}
                                                    ghostUnitIds={releasedUnitIds}
                                                />
                                            )}
                                            {!isUnitsQueryLoading && (
                                                <UnitsSummary
                                                    label="To Dispatch"
                                                    units={toDispatchUnits}
                                                />
                                            )}
                                        </Box>
                                    </VStack>
                                    <Flex my="3" flexDirection="column" gap="half">
                                        <Input
                                            isClearable
                                            autoFocus
                                            data-testid="manage-units-text-filter-input"
                                            leftIcon="Search"
                                            size="sm"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                debouncedSetQuery(e.currentTarget.value);
                                            }}
                                        />
                                        <Text color="tertiary" variant="bodyMd">
                                            Search by Unit Call Sign, Location, Status, Type, or
                                            Skill
                                        </Text>
                                    </Flex>
                                    <HStack gap="1">
                                        <FilterPopover
                                            selectedFilters={selectedFilters}
                                            setSelectedFilters={setSelectedFilters}
                                            hasSelectedFilters={hasSelectedFilters}
                                        />
                                        <SortMenu
                                            sortDirection={sortDirection}
                                            sortingParam={sortingParam}
                                            onChangeSortDirection={setSortDirection}
                                            onChangeSortingParam={setSortingParam}
                                        />
                                    </HStack>
                                </>
                            )}
                            {unitLevelDispositionsEnabled &&
                                primaryDispositionInfo &&
                                primaryDispositionInfo.isPrimary &&
                                primaryDispositionInfo.unitCallSign && (
                                    <BannersDisplay
                                        banners={[
                                            {
                                                status: 'info',
                                                title: 'Primary disposition added',
                                                description: (
                                                    <Flex>
                                                        <Text variant="headingSm">
                                                            {`${primaryDispositionInfo.unitCallSign}`}
                                                        </Text>
                                                        &nbsp;
                                                        <Text>{` - ${
                                                            primaryDispositionInfo
                                                                .dispositionAttributeOption
                                                                ?.label ?? ''
                                                        }`}</Text>
                                                    </Flex>
                                                ),
                                            },
                                        ]}
                                    />
                                )}
                            <BannersDisplay banners={banners} />
                        </DrawerHeader>
                        <DrawerBody
                            style={{
                                paddingTop: '8px',
                                paddingRight: 0,
                                paddingBottom: 0,
                                // Positioning context for the floating loading/ready pills.
                                position: 'relative',
                            }}
                        >
                            {isUnitsQueryError && (
                                <Flex w="100%" h="100%" justifyContent="center" alignItems="center">
                                    <InlineBanner
                                        status="error"
                                        description="An error occurred while loading data"
                                    />
                                </Flex>
                            )}
                            {isUnitsQueryLoading ? (
                                <LoadingIndicator />
                            ) : (
                                <>
                                    {/* Peek chips: floating Arc Buttons mirroring the Recommended
                                        section's status while it's >50% scrolled out of view.
                                        Absolute (inside the gated position:relative DrawerBody) => no
                                        flow height => Virtuoso stays the sole in-flow child => a
                                        single scrollbar. The inline section + spinner live in the
                                        Virtuoso Header (RecommendedSectionHeader). */}
                                    {showLoadingPeekChip && (
                                        <RecommendationPeekChip
                                            state="loading"
                                            onClick={scrollToRecommendedSection}
                                        />
                                    )}
                                    {showReadyPeekChip && (
                                        <RecommendationPeekChip
                                            state="ready"
                                            onClick={handleReadyPeekChipClick}
                                        />
                                    )}
                                    <GroupedVirtuoso
                                        ref={keyboardTileNavigation.virtuosoRef}
                                        tabIndex={-1}
                                        groupCounts={groupCounts}
                                        groupContent={renderGroupContent}
                                        style={{
                                            height: '100%',
                                            width: '100%',
                                            opacity: refreshDimOpacity(isResponsePlanRefreshing),
                                            transition: 'opacity 150ms',
                                        }}
                                        rows={rows.length}
                                        itemContent={renderItemContent}
                                        {...asyncVirtuosoProps}
                                    />
                                </>
                            )}
                        </DrawerBody>
                    </>
                )}
                <DrawerFooter>
                    <ButtonGroup>
                        <Button
                            onFocus={keyboardTileNavigation.clearKeyboardNavState}
                            variant="ghost"
                            onClick={closeDrawer}
                            isDisabled={isMutationLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            isDisabled={isEventQueryLoading || isUnitsQueryLoading}
                            isLoading={isMutationLoading}
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </ButtonGroup>
                </DrawerFooter>
            </Flex>
        </Drawer>
    );
});

export type AsyncManageUnitsDrawerType = typeof AsyncManageUnitsDrawer;
